import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch, AppState } from "../../redux/store";
import { SingleValue, MultiValue } from "react-select";
import EmptyDataOverlay from "../../components/empty-data-overlay";
import Loading from "../../components/loading";
import StyledSelect from "../../components/styled-select";
import Table from "../../components/table";
import TableBodyCell from "../../components/table-body-cell";
import ToastComponent from "../../components/toast";
import PermissionGate from "../../core/layouts/permission-gate";
import { isSelectSingleValue } from "../../core/utils";
import paths from "../../navigation/paths";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../redux/features/common-slice";
import {
  getAllJDsState,
  allJDsStateAction,
  JDStatus,
  JobType,
  JDResponse,
} from "../../redux/features/jd-slice";
import { getUserState } from "../../redux/features/auth-slice";
import { OptionValue } from "../../types/component";
import Button from "../../components/button";
import { UserRole } from "../../redux/features/user-slice";
import { format } from "date-fns/format";

export default function JDsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allJDsState = useSelector(getAllJDsState);
  const errorState = useSelector(getErrorState);
  const userState = useSelector(getUserState);

  const [selectedStatuses, setSelectedStatuses] = useState<JDStatus[]>([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobType[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchAllJDs = useCallback(async () => {
    await dispatch(
      allJDsStateAction({
        status: selectedStatuses,
        jobType: selectedJobTypes,
      })
    );
  }, [dispatch, selectedStatuses, selectedJobTypes]);

  useEffect(() => {
    fetchAllJDs();
  }, [fetchAllJDs]);

  useEffect(() => {
    dispatch(
      setBreadcrumbState([{ id: "jds", path: null, title: "Job Descriptions" }])
    );
  }, [dispatch]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
      } else {
        toast(errorState.message, { type: "error" });
      }
    }
  }, [errorState, navigate]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortIcon = (key: string) =>
    sortKey !== key ? ' ↕' : sortDirection === 'asc' ? ' ↑' : ' ↓';

  const displayedJDs = [...allJDsState.data].sort((a, b) => {
    if (!sortKey) return 0;
    let valA: string | number = '';
    let valB: string | number = '';
    switch (sortKey) {
      case 'title': valA = a.title.toLowerCase(); valB = b.title.toLowerCase(); break;
      case 'deadline': valA = new Date(a.deadline).getTime(); valB = new Date(b.deadline).getTime(); break;
      case 'jobType': valA = a.jobType.toLowerCase(); valB = b.jobType.toLowerCase(); break;
      case 'location': valA = (a.location ?? '').toLowerCase(); valB = (b.location ?? '').toLowerCase(); break;
      case 'status': valA = a.status.toLowerCase(); valB = b.status.toLowerCase(); break;
      case 'createdDate': valA = new Date(a.createdAt).getTime(); valB = new Date(b.createdAt).getTime(); break;
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <PermissionGate categories={[]} roles={[UserRole.Admin, UserRole.Contributor]}>
      <Helmet title="All Job Descriptions" />
      <div className="flex items-center justify-between mb-4">
        <Button
          label="Add job description"
          onClick={() => navigate(paths.ADD_JD)}
          variant="filled"
        />
        <div className="flex space-x-2">
          <StyledSelect
            options={Object.values(JDStatus).map((e: string) => ({
              value: e,
              label: e.charAt(0).toUpperCase() + e.slice(1),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedStatuses.length > 0
                  ? `${selectedStatuses.length} status${selectedStatuses.length > 1 ? "es" : ""} selected`
                  : "All statuses",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedStatuses.includes(value.value as JDStatus)) {
                  setSelectedStatuses(
                    selectedStatuses.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedStatuses([
                    ...selectedStatuses,
                    value.value as JDStatus,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background:
                selectedStatuses.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            })}
          />
          <StyledSelect
            options={Object.values(JobType).map((e: string) => ({
              value: e,
              label: e
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
            }))}
            isClearable={false}
            value={{
              value: "",
              label:
                selectedJobTypes.length > 0
                  ? `${selectedJobTypes.length} job type${selectedJobTypes.length > 1 ? "s" : ""} selected`
                  : "All job types",
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                if (selectedJobTypes.includes(value.value as JobType)) {
                  setSelectedJobTypes(
                    selectedJobTypes.filter((e) => e !== value.value)
                  );
                } else {
                  setSelectedJobTypes([
                    ...selectedJobTypes,
                    value.value as JobType,
                  ]);
                }
              }
            }}
            exClassName="w-48 z-20"
            exOption={(value: OptionValue) => ({
              background:
                selectedJobTypes.findIndex((e) => e === value.value) !== -1
                  ? "#f1f5f9"
                  : undefined,
            })}
          />
        </div>
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          { id: "title", label: `Title${sortIcon('title')}`, align: "text-left", width: "w-3/12", onClick: () => handleSort('title') },
          { id: "deadline", label: `Deadline${sortIcon('deadline')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('deadline') },
          { id: "jobType", label: `Job Type${sortIcon('jobType')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('jobType') },
          { id: "location", label: `Location${sortIcon('location')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('location') },
          { id: "status", label: `Status${sortIcon('status')}`, align: "text-left", width: "w-1/12", onClick: () => handleSort('status') },
          { id: "createdDate", label: `Created date${sortIcon('createdDate')}`, align: "text-left", width: "w-2/12", onClick: () => handleSort('createdDate') },
        ]}
        body={
          allJDsState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={5} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : displayedJDs.length > 0
            ? displayedJDs.map((jd: JDResponse) => (
                <tr
                  key={jd.id}
                  className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                  onClick={() => navigate(`${paths.JDS}/${jd.id}`)}
                >
                  <TableBodyCell>
                    <label className="whitespace-normal break-words hover:underline cursor-pointer">
                      {jd.title}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {format(jd.deadline, "yyyy-MM-dd") || "--"}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words capitalize">
                      {jd.jobType
                        .split('_')
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {jd.location || "--"}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="whitespace-normal break-words capitalize">
                      {jd.status}
                    </label>
                  </TableBodyCell>
                   <TableBodyCell>
                    <label className="whitespace-normal break-words">
                      {format(jd.createdAt, "yyyy-MM-dd") || "--"}
                    </label>
                  </TableBodyCell>
                </tr>
              ))
            : [
                <tr key="empty-row">
                  <td colSpan={5} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No JDs found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allJDs" />
    </PermissionGate>
  );
}